import React, { useEffect, useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { getUser, logout } from '../../services/helpers'
import {
  CalendarIcon,
  TicketIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  TagIcon,
  ArrowRightIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'
import MobileBottomNav from '../../components/MobileBottomNav'
import Logo from '../../asset/images/logo.svg'

const AttendeeDashboardPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState(null)
  const displayName = user?.name || user?.email || 'Attendee'

  useEffect(() => {
    const u = getUser()
    setUser(u)
  }, [])

  const menuItems = [
    { 
      id: 'events', 
      label: 'Discover Events', 
      icon: CalendarIcon,
      path: '/events',
      description: 'Browse and book tickets for upcoming events',
      color: 'from-emerald-500 to-emerald-600',
      disabled: true
    },
    { 
      id: 'offers', 
      label: 'Browse Offers', 
      icon: TagIcon,
      path: '/offers',
      description: 'Find exclusive deals and discounts',
      color: 'from-amber-500 to-amber-600'
    },
    { 
      id: 'centers', 
      label: 'Centers', 
      icon: BuildingOffice2Icon,
      path: '/centers',
      description: 'Browse centers and their profiles',
      color: 'from-blue-500 to-blue-600',
      disabled: true
    },
    { 
      id: 'tickets', 
      label: 'My Tickets', 
      icon: TicketIcon,
      path: '/account/tickets',
      description: 'View and manage your purchased tickets',
      color: 'from-emerald-500 to-emerald-600'
    },
    { 
      id: 'orders', 
      label: 'Order History', 
      icon: ShoppingBagIcon,
      path: '/account/orders',
      description: 'Track all your transactions',
      color: 'from-amber-500 to-amber-600'
    },
    { 
      id: 'disputes', 
      label: 'Disputes', 
      icon: ExclamationTriangleIcon,
      path: '/account/disputes',
      description: 'Manage and resolve issues',
      color: 'from-red-500 to-red-600'
    },
    { 
      id: 'profile', 
      label: 'Profile', 
      icon: UserCircleIcon,
      path: '/account/profile',
      description: 'Update your account information',
      color: 'from-slate-500 to-slate-600'
    }
  ]

  // Determine active item based on current path
  const getActiveItem = () => {
    const currentPath = location.pathname
    if (currentPath === '/account' || currentPath === '/account/') {
      return 'events' // Default to events if on dashboard
    }
    // Check for centers paths
    if (currentPath.startsWith('/centers')) {
      return 'centers'
    }
    return menuItems.find(item => currentPath.startsWith(item.path))?.id || 'events'
  }

  const activeItemId = getActiveItem()

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      {/* Mobile Header - Default, hidden on desktop */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 lg:hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 h-14 safe-area-inset-top">
          <Link to="/" className="flex items-center gap-1.5">
            <img
              src={Logo}
              alt="Obule"
              className="h-8 sm:h-9 w-auto object-contain"
            />
            <span className="text-xl sm:text-2xl font-semibold tracking-wide text-slate-900">bule</span>
          </Link>
          <Link to="/account/profile" className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center active:scale-95 transition-transform min-w-[44px] min-h-[44px]">
            <UserCircleIcon className="w-6 h-6 text-slate-600" />
          </Link>
        </div>
      </header>

      {/* Desktop Sidebar - Hidden on mobile, shown on lg+ */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-1.5 group">
              <img
                src={Logo}
                alt="Obule"
                className="h-9 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
              />
              <span className="text-xl font-semibold tracking-wide text-slate-900">bule</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {menuItems.filter(item => !item.disabled).map((item) => {
              const Icon = item.icon
              const isActive = activeItemId === item.id
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {item.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/auth/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col pb-20 lg:pb-0 lg:ml-64">
        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="w-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Welcome Header - Mobile First, Enhanced on Desktop */}
            <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-5 sm:py-6 mb-4 sm:mb-6 lg:hidden">
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">
                Welcome back!
              </h1>
              <p className="text-base sm:text-lg text-slate-600">
                {displayName}
              </p>
            </div>

            {/* Desktop Welcome */}
            <div className="hidden lg:block mb-12 pt-8">
              <h1 className="text-4xl xl:text-5xl font-bold text-slate-900 mb-3">
                Welcome back, {displayName}!
              </h1>
              <p className="text-lg xl:text-xl text-slate-600">
                What would you like to do today?
              </p>
            </div>

            {/* Cards Grid - Mobile First, Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 pb-6 sm:pb-8 lg:pb-12">
              {menuItems.filter(item => item.id !== 'home' && !item.disabled).map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className="group relative bg-white rounded-xl sm:rounded-2xl border border-slate-200 sm:border-2 overflow-hidden active:scale-[0.98] sm:active:scale-100 transition-all duration-200 sm:duration-300 hover:border-emerald-500 hover:shadow-xl lg:hover:shadow-2xl"
                  >
                    {/* Mobile Card Layout */}
                    <div className="lg:hidden flex items-center gap-4 sm:gap-5 p-5 sm:p-6">
                      <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2">
                          {item.label}
                        </h3>
                        <p className="text-sm sm:text-base text-slate-600 line-clamp-2">
                          {item.description}
                        </p>
                      </div>
                      <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 flex-shrink-0" />
                    </div>

                    {/* Desktop Card Layout */}
                    <div className="hidden lg:block p-8">
                      {/* Gradient Background */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                      
                      {/* Content */}
                      <div className="relative">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                          {item.label}
                        </h3>
                        
                        <p className="text-slate-600 text-sm mb-4">
                          {item.description}
                        </p>
                        
                        <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm group-hover:gap-3 transition-all">
                          <span>Go to {item.label}</span>
                          <ArrowRightIcon className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav />
    </div>
  )
}

export default AttendeeDashboardPage